// kommunikationHelpers.ts — reine, testbare Logik des Kontakt-Tabs
// „Kommunikation" (SP4). Bewusst frei von React/DOM, damit die Pagination,
// die Leere-Sektion-Erkennung und die Archiv-Semantik (Entscheidung 74)
// isoliert per Vitest geprüft werden können.

import type { Email } from "../services/graphMailService";
import type { MailLinkForContactDto } from "../services/mailLinkService";

/** Initiale Sichtbarkeit einer Sektion + Schrittweite von „mehr laden". */
export const KOMM_PAGE_SIZE = 10;

/** Auf die aktuell sichtbare Seite kürzen (defensiv gegen negative Limits). */
export function visibleUpTo<T>(items: T[], limit: number): T[] {
    return items.slice(0, Math.max(0, limit));
}

/**
 * Gibt es clientseitig noch mehr Einträge, als aktuell sichtbar sind?
 * (Mails werden clientseitig gesliced — die Datenquelle ist der gecachte Fetch.)
 */
export function hasMoreClient(total: number, limit: number): boolean {
    return limit < total;
}

/**
 * Sind serverseitig noch mehr Einträge nachladbar? (Anrufe kommen über
 * `listCalls({ limit, offset })` + `total` seitenweise vom Backend.)
 */
export function hasMoreServer(loaded: number, total: number): boolean {
    return loaded < total;
}

/**
 * Adressen einer Mail (Absender + To + Cc), lowercase — Basis für den
 * Adress-Match (SP5/A3).
 */
export function mailAddressSet(e: {
    senderEmail?: string;
    to?: { address?: string }[];
    cc?: { address?: string }[];
}): Set<string> {
    const s = new Set<string>();
    if (e.senderEmail) s.add(e.senderEmail.toLowerCase());
    for (const r of e.to ?? []) if (r?.address) s.add(r.address.toLowerCase());
    for (const r of e.cc ?? []) if (r?.address) s.add(r.address.toLowerCase());
    return s;
}

/**
 * Passt eine Mail zum gerade geöffneten Kontakt?
 *  - Primär-Match: der Kontakt ist der `matchedContact` der Mail;
 *  - Sekundär-Match (SP5): der Kontakt ist einer der `matchedContactIds`
 *    (alle SICHTBAREN from/to/cc-Treffer, z. B. als CC);
 *  - Adress-Match (SP5/A3): eine der `relevantAddresses` (z. B. die
 *    GESCHÄFTLICHEN Ansprechpartner-Adressen einer Firma, Entscheidung 71)
 *    kommt in from/to/cc der Mail vor. Damit erscheinen AP-geschäftlich-Mails
 *    auch in der FIRMEN-Ansicht, obwohl die Firma selbst nicht im Mail-Header
 *    steht. Die Mails sind serverseitig bereits RBAC-gefiltert — der
 *    Adress-Match verengt nur auf den geöffneten Kontakt (keine neue Sicht).
 */
export function mailMatchesContact(
    e: {
        matchedContact?: { id: number } | null;
        matchedContactIds?: number[];
        senderEmail?: string;
        to?: { address?: string }[];
        cc?: { address?: string }[];
    },
    contactId: number,
    relevantAddresses?: Set<string>,
): boolean {
    if (e.matchedContact?.id === contactId) return true;
    if (e.matchedContactIds?.includes(contactId)) return true;
    if (relevantAddresses && relevantAddresses.size > 0) {
        for (const a of mailAddressSet(e)) if (relevantAddresses.has(a)) return true;
    }
    return false;
}

/**
 * Echte Archiv-Semantik (Entscheidung 74, #763–#769): Der Posteingang ist der
 * ECHTE Graph-Inbox-Ordner (gelesen UND ungelesen), das Archiv der ECHTE
 * Graph-Archiv-Ordner — NICHT mehr der frühere gelesen/ungelesen-Split. Beide
 * Listen werden auf den geöffneten Kontakt gefiltert.
 */
export function splitContactMails(
    inbox: Email[],
    archive: Email[],
    contactId: number,
    relevantAddresses?: Set<string>,
): { posteingang: Email[]; archiv: Email[] } {
    return {
        posteingang: inbox.filter((e) => mailMatchesContact(e, contactId, relevantAddresses)),
        archiv: archive.filter((e) => mailMatchesContact(e, contactId, relevantAddresses)),
    };
}

/** Ergebnis der Sektions-Sichtbarkeit im Kommunikations-Tab. */
export interface SectionVisibility {
    /** Mail-Sektion rendern? (nur wenn nicht nachweislich leer) */
    showMails: boolean;
    /** Anruf-Sektion rendern? (nur wenn nicht nachweislich leer) */
    showCalls: boolean;
    /** Beide Sektionen leer → EIN gemeinsamer Leerzustand. */
    allEmpty: boolean;
}

/**
 * „Leere Sektion nicht anzeigen" (A2): eine Sektion gilt erst als leer, wenn ihr
 * Laden abgeschlossen ist und nichts geliefert hat. Solange geladen/fehlerhaft
 * wird, bleibt die Sektion sichtbar. Sind BEIDE leer, zeigt der Tab stattdessen
 * einen einzigen gemeinsamen Leerzustand.
 *
 * Wichtig — Mail- und Anruf-Sektion werden SYMMETRISCH behandelt: ein
 * Ladefehler (`mailError` bzw. `callsError`) hält die jeweilige Sektion sichtbar,
 * damit sie ihre EIGENE Fehlermeldung anzeigen kann. Andernfalls würde ein
 * fehlgeschlagener Mail-Abruf (Graph-429/5xx/abgelaufenes Token) fälschlich als
 * leere Mail-Sektion gewertet und — hat der Kontakt keine Anrufe — der
 * irreführende gemeinsame Leerzustand „Keine Kommunikation" erscheinen.
 */
export function sectionVisibility(args: {
    mailLoaded: boolean;
    mailCount: number;
    mailError: boolean;
    callsLoading: boolean;
    callsError: boolean;
    callsTotal: number;
}): SectionVisibility {
    const mailsEmpty = args.mailLoaded && !args.mailError && args.mailCount === 0;
    const callsEmpty = !args.callsLoading && !args.callsError && args.callsTotal === 0;
    return {
        showMails: !mailsEmpty,
        showCalls: !callsEmpty,
        allEmpty: mailsEmpty && callsEmpty,
    };
}

// ── F1/F2: die einem Kontakt zugeordneten Mails ──────────────────────────────
// Der Adress-Match oben findet nur, was im aktuellen Postfach liegt und den
// Kontakt im Kopf trägt. Eine Mail, die einem VORGANG des Kontakts zugewiesen
// wurde, erfüllt beides oft nicht — sie liegt im CBB-Postfach, in einem
// Unterordner, oder sie ist an eine Adresse gegangen, die am Kontakt nie
// hinterlegt wurde. Deshalb kommt sie aus der Verknüpfungstabelle
// (`GET /mail/links/kontakt/:id`) und wird hier eingemischt.

/** Der Vorgang, zu dem eine Mail gehört — Anzeige-Kennzeichnung (F2). */
export type MailVorgangRef = { id: number; titel: string };

const initialenVon = (name?: string | null): string =>
    (name || "?")
        .split(/\s+/)
        .map((teil) => teil[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();

/**
 * Synthetische `Email` aus dem Snapshot einer Verknüpfung — genug zum Öffnen
 * des Detailfensters, das Betreff/Body über `mailbox` + `messageId` nachlädt.
 *
 * `hasAttachments` kommt aus der Sicherung (`archive.anhaenge`): der Snapshot
 * kennt das Graph-Flag nicht. Bis T-170 (28.10.2026) stand hier pauschal
 * `true`, damit ein Detailfenster die Anhänge nachlädt — beide Aufrufer öffnen
 * Snapshot-Zeilen aber über `openLinkedMailWindow`, das Inhalt und Anhänge
 * selbst lädt. Das Flag zeichnet jetzt die Büroklammer der Kontaktansicht.
 */
export function emailAusMailLink(l: MailLinkForContactDto): Email {
    return {
        id: l.messageId,
        conversationId: l.conversationId ?? "",
        senderName: l.senderName ?? "Unbekannt",
        senderEmail: l.senderEmail ?? "",
        senderInitial: initialenVon(l.senderName),
        senderColor: "#4d2051",
        subject: l.subject ?? "(Kein Betreff)",
        date: l.receivedAt ? new Date(l.receivedAt) : new Date(0),
        dateLabel: "",
        preview: l.preview ?? "",
        body: "",
        hasAttachments: (l.archive?.anhaenge ?? 0) > 0,
        type: "normal",
        read: true,
    } as unknown as Email;
}

/**
 * Nachschlagewerk „welcher Vorgang gehört zu dieser Nachricht?" (F2) — für die
 * Kennzeichnung an den Zeilen von Posteingang und Archiv. Schlüssel ist die
 * Graph-`messageId`; Verknüpfungen ohne auflösbaren Vorgang bleiben außen vor.
 */
export function vorgangJeMessageId(
    links: MailLinkForContactDto[] | undefined,
): Map<string, MailVorgangRef> {
    const out = new Map<string, MailVorgangRef>();
    for (const l of links ?? []) {
        if (l.vorgang && l.messageId && !out.has(l.messageId)) out.set(l.messageId, l.vorgang);
    }
    return out;
}

/**
 * Die zugewiesenen Mails, die in Posteingang/Archiv NICHT ohnehin schon stehen.
 * Genau diese muss die Kontaktansicht zusätzlich zeigen — sonst fehlte alles,
 * was in einem anderen Postfach oder Ordner liegt (F1). Reihenfolge: neueste
 * zuerst; der Server liefert bereits so sortiert, wir sortieren defensiv nach.
 */
export function nurZusaetzlicheLinks(
    posteingang: Email[],
    archiv: Email[],
    links: MailLinkForContactDto[] | undefined,
): MailLinkForContactDto[] {
    const bekannt = new Set<string>();
    for (const e of posteingang) bekannt.add(e.id);
    for (const e of archiv) bekannt.add(e.id);
    return (links ?? [])
        .filter((l) => !bekannt.has(l.messageId))
        .sort((a, b) => zeitVon(b) - zeitVon(a));
}

const zeitVon = (l: MailLinkForContactDto): number => {
    const t = l.receivedAt ? new Date(l.receivedAt).getTime() : 0;
    return Number.isNaN(t) ? 0 : t;
};

/**
 * Gesamtzahl der Mails eines Kontakts für die Sichtbarkeit der Sektion (A2):
 * Posteingang + Archiv + die zugewiesenen, die dort nicht ohnehin schon stehen.
 * Ohne den dritten Summanden verschwände die Mail-Sektion bei einem Kontakt,
 * dessen Post ausschließlich über Vorgänge zugeordnet ist.
 */
export function zaehleKontaktMails(
    posteingang: Email[],
    archiv: Email[],
    links: MailLinkForContactDto[] | undefined,
): number {
    return (
        posteingang.length +
        archiv.length +
        nurZusaetzlicheLinks(posteingang, archiv, links).length
    );
}

// ── Archiv-Verlauf: dedupliziert und nach Vorgang gruppiert ─────────────────

export interface ArchivMailZeile {
    key: string;
    email: Email;
    mailbox: string;
    vorgang: MailVorgangRef | null;
    link?: MailLinkForContactDto;
}

export interface ArchivGruppe {
    key: string;
    titel: string;
    vorgang: MailVorgangRef | null;
    mails: ArchivMailZeile[];
}

const archivMailKey = (mailbox: string, messageId: string): string =>
    `${mailbox.trim().toLowerCase()}::${messageId}`;

const mailZeit = (email: Email): number => {
    const zeit = email.date instanceof Date ? email.date.getTime() : 0;
    return Number.isNaN(zeit) ? 0 : zeit;
};

/**
 * Führt Graph-Archiv und MailLink-Snapshots zusammen. Der exakte Schlüssel aus
 * Postfach + Message-ID verhindert Kollisionen zwischen Postfächern; ein Link
 * reichert eine bereits vorhandene Graph-Zeile um ihren Vorgang an.
 */
export function baueArchivGruppen(
    archiv: Email[],
    links: MailLinkForContactDto[] | undefined,
    aktuellesPostfach: string | null | undefined,
): ArchivGruppe[] {
    const zeilen = new Map<string, ArchivMailZeile>();
    const graphMailbox = aktuellesPostfach ?? "";

    for (const email of archiv) {
        const key = archivMailKey(graphMailbox, email.id);
        zeilen.set(key, { key, email, mailbox: graphMailbox, vorgang: null });
    }
    for (const link of links ?? []) {
        const key = archivMailKey(link.mailbox, link.messageId);
        const vorhanden = zeilen.get(key);
        if (vorhanden) {
            zeilen.set(key, {
                ...vorhanden,
                vorgang: link.vorgang ?? vorhanden.vorgang,
                link,
            });
        } else {
            zeilen.set(key, {
                key,
                email: emailAusMailLink(link),
                mailbox: link.mailbox,
                vorgang: link.vorgang ?? null,
                link,
            });
        }
    }

    const gruppen = new Map<string, ArchivGruppe>();
    const sortiert = Array.from(zeilen.values()).sort((a, b) => mailZeit(b.email) - mailZeit(a.email));
    for (const zeile of sortiert) {
        const key = zeile.vorgang ? `vorgang:${zeile.vorgang.id}` : "ohne-vorgang";
        let gruppe = gruppen.get(key);
        if (!gruppe) {
            gruppe = {
                key,
                titel: zeile.vorgang?.titel ?? "Ohne Vorgang",
                vorgang: zeile.vorgang,
                mails: [],
            };
            gruppen.set(key, gruppe);
        }
        gruppe.mails.push(zeile);
    }
    return Array.from(gruppen.values()).sort((a, b) => {
        if (a.vorgang && !b.vorgang) return -1;
        if (!a.vorgang && b.vorgang) return 1;
        return mailZeit(b.mails[0]?.email) - mailZeit(a.mails[0]?.email);
    });
}

const formatAdresse = (name?: string | null, adresse?: string | null): string => {
    const n = (name ?? "").trim();
    const a = (adresse ?? "").trim();
    if (n && a) return `${n} <${a}>`;
    return n || a || "—";
};

/** Spaltenwerte einer Kommunikationszeile, unabhängig von ihrer Datenquelle. */
export function mailMetadaten(
    email: Email,
    link?: MailLinkForContactDto,
): { von: string; an: string; datum: string; uhrzeit: string } {
    const empfaenger = link?.recipients?.trim()
        ? link.recipients.trim()
        : (email.to ?? []).map((e) => formatAdresse(e.name, e.address)).join(", ") || "—";
    const date = email.date instanceof Date ? email.date : new Date(0);
    const gueltig = !Number.isNaN(date.getTime()) && date.getTime() > 0;
    return {
        von: formatAdresse(email.senderName, email.senderEmail),
        an: empfaenger,
        datum: gueltig
            ? date.toLocaleDateString("de-DE", {
                  timeZone: "Europe/Berlin",
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
              })
            : "—",
        uhrzeit: gueltig
            ? date.toLocaleTimeString("de-DE", {
                  timeZone: "Europe/Berlin",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
              })
            : "—",
    };
}
